import React, { useState, useEffect } from "react";
import Button from "rc-app/components/Button";
import UserService from "rc-app/services/UserService";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { createReCustomer } from "rc-app/api/customerActionsApi";
import { getAgentProfile } from "rc-app/api/agentActionsApi";
import { useLocation, useNavigate } from "react-router-dom";
interface FormErrors {
  first_name?: string;
  last_name?: string;
  gender?: string;
  mobile_number?: string;
  email_id?: string;
  address_line1?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  form?: string;
  [key: string]: string | undefined; // Index signature for any other field
}

export const AddCustomer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [gender, setGender] = useState("");
  const [agentId, setAgentId] = useState("");

  const [customerDetails, setCustomerDetails] = useState({
    first_name: "",
    last_name: "",
    gender: "",
    mobile_number: "",
    email_id: "",
    date_of_birth: "",
    property_shortlist: [],
    address: {
      address_line1: "",
      address_line2: "",
      landmark: "",
      postal_code: "",
      province: "",
      city: "",
      country: "",
    },
    agent_id: [],
    joined_date: new Date().toISOString().split("T")[0], // Current date in YYYY-MM-DD format
  });

  useEffect(() => {
    getAgentProfile(UserService.getUseremail())
      .then((res: any) => {
        setAgentId(res.agent?._id);
      })
      .catch((err) => console.log(err));
  }, []);

  // Validation function
  const validateForm = () => {
    const newErrors: FormErrors = {};

    // Required fields validation
    if (!customerDetails.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!customerDetails.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    if (!gender) {
      newErrors.gender = "Gender is required";
    }

    if (!customerDetails.mobile_number.trim()) {
      newErrors.mobile_number = "Mobile number is required";
    } else if (!/^\d{10}$/.test(customerDetails.mobile_number)) {
      newErrors.mobile_number = "Mobile number must be 10 digits";
    }

    if (!customerDetails.email_id.trim()) {
      newErrors.email_id = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerDetails.email_id)) {
      newErrors.email_id = "Invalid email format";
    }

    // Address validation
    if (!customerDetails.address.address_line1.trim()) {
      newErrors.address_line1 = "Address line 1 is required";
    }

    if (!customerDetails.address.postal_code.trim()) {
      newErrors.postal_code = "Pincode is required";
    } else if (!/^\d{6}$/.test(customerDetails.address.postal_code)) {
      newErrors.postal_code = "Pincode must be 6 digits";
    }

    if (!customerDetails.address.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!customerDetails.address.country.trim()) {
      newErrors.country = "Country is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGenderChange = (e: any) => {
    const selectedGender = e.target.value;
    setGender(selectedGender);
    setCustomerDetails({ ...customerDetails, gender: selectedGender });
    // Clear gender error if user selects one
    if (errors.gender) {
      setErrors({ ...errors, gender: "" });
    }
  };

  const onInputChange = (e: any) => {
    const { name, value } = e.target;

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }

    // Handle address fields
    const addressFields = [
      "address_line1",
      "address_line2",
      "landmark",
      "postal_code",
      "province",
      "city",
      "country",
    ];

    if (addressFields.includes(name)) {
      setCustomerDetails({
        ...customerDetails,
        address: { ...customerDetails.address, [name]: value },
      });
    } else {
      setCustomerDetails({ ...customerDetails, [name]: value });
    }
  };

  const handleSaveChanges = async () => {
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setIsLoading(true);

    try {
      const jsonData = {
        ...customerDetails,
        agent_id: [agentId],
        gender: gender, // Ensure gender is included
        address: {
          ...customerDetails.address,
          postal_code: customerDetails.address.postal_code, // Renamed from landmark in your form
        },
      };

      const res = await createReCustomer(jsonData);
      console.log("Customer created successfully:", res);

      navigate(`/customers`, {
        state: {
          breadcrumbs: [
            { path: "/", label: "Home" },
            { path: "/customers", label: "My Customers" },
          ],
          referrer: "/customers",
        },
      });
    } catch (error) {
      console.error("Error creating customer:", error);
      // You might want to show an error message to the user here
      setErrors({
        ...errors,
        form: "Failed to create customer. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to render error message
  const renderError = (fieldName: any) => {
    if (errors[fieldName]) {
      return <p className="text-red-500 text-sm mt-1">{errors[fieldName]}</p>;
    }
    return null;
  };

  useEffect(() => {
    if (!location.state?.breadcrumbs) {
      navigate(`/add_customer`, {
        state: {
          breadcrumbs: [
            { path: "/", label: "Home" },
            { path: "/customers", label: "My Customers" },
            {
              path: "/add_customer",
              label: "Add Customer",
            },
          ],
        },
      });
    }
  }, [location.state]);

  return (
    <>
      <div className="flex w-full flex-col max-xl:pb-2 xl:flex-row gap-4 px-2">
        <div className="flex flex-col xl:py-4 gap-3 w-full">
          <div className="flex flex-col w-full">
            {/* Form error message */}
            {errors.form && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {errors.form}
              </div>
            )}

            <form className="flex gap-3 flex-col w-full">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 w-full gap-4">
                {/* First Name */}
                <div className="flex flex-col">
                  <label
                    htmlFor="first_name"
                    className="text-xl text-[#889DB2] font-normal"
                  >
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={customerDetails.first_name}
                    onChange={onInputChange}
                    className={`px-4 py-[7px] text-[#F4F5F7] text-xl bg-[#2E4051] rounded-md outline-none focus:ring-[#008564] focus:border-[#008564] ${
                      errors.first_name ? "border border-red-500" : ""
                    }`}
                    required
                  />
                  {renderError("first_name")}
                </div>

                {/* Last Name */}
                <div className="flex flex-col">
                  <label
                    htmlFor="last_name"
                    className="text-xl text-[#889DB2] font-normal"
                  >
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={customerDetails.last_name}
                    onChange={onInputChange}
                    className={`px-4 py-[7px] text-[#F4F5F7] text-xl bg-[#2E4051] rounded-md outline-none focus:ring-[#008564] focus:border-[#008564] ${
                      errors.last_name ? "border border-red-500" : ""
                    }`}
                    required
                  />
                  {renderError("last_name")}
                </div>

                {/* Gender */}
                <div className="flex flex-col">
                  <label className="text-xl text-[#889DB2] font-normal">
                    Gender *
                  </label>
                  <div className="flex w-full items-center md:py-[7px] gap-3">
                    <div
                      className="flex items-center cursor-pointer"
                      onClick={() =>
                        handleGenderChange({ target: { value: "male" } })
                      }
                    >
                      <input
                        type="radio"
                        id="male"
                        name="gender"
                        value="male"
                        checked={gender === "male"}
                        className="mr-2 text-[#55B7E4] outline-none"
                        onChange={handleGenderChange}
                      />
                      <label htmlFor="male" className="text-[#F4F5F7] text-xl">
                        Male
                      </label>
                    </div>
                    <div
                      className="flex items-center cursor-pointer"
                      onClick={() =>
                        handleGenderChange({ target: { value: "female" } })
                      }
                    >
                      <input
                        type="radio"
                        id="female"
                        name="gender"
                        value="female"
                        checked={gender === "female"}
                        className="mr-2 outline-none text-[#55B7E4]"
                        onChange={handleGenderChange}
                      />
                      <label
                        htmlFor="female"
                        className="text-[#F4F5F7] text-xl"
                      >
                        Female
                      </label>
                    </div>
                  </div>
                  {errors.gender && (
                    <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
                  )}
                </div>

                {/* Mobile Number */}
                <div className="flex flex-col">
                  <label
                    htmlFor="mobile_number"
                    className="text-xl text-[#889DB2] font-normal"
                  >
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    id="mobile_number"
                    name="mobile_number"
                    value={customerDetails?.mobile_number}
                    onChange={onInputChange}
                    className={`px-4 py-[7px] text-[#F4F5F7] text-xl bg-[#2E4051] rounded-md outline-none focus:ring-[#008564] focus:border-[#008564] ${
                      errors.mobile_number ? "border border-red-500" : ""
                    }`}
                    maxLength={10}
                    required
                  />
                  {renderError("mobile_number")}
                </div>

                {/* Email */}
                <div className="flex flex-col">
                  <label
                    htmlFor="email_id"
                    className="text-xl text-[#889DB2] font-normal"
                  >
                    Email Id *
                  </label>
                  <input
                    type="email"
                    id="email_id"
                    name="email_id"
                    value={customerDetails.email_id}
                    onChange={onInputChange}
                    className={`px-4 py-[7px] text-[#F4F5F7] text-xl bg-[#2E4051] rounded-md outline-none focus:ring-[#008564] focus:border-[#008564] ${
                      errors.email_id ? "border border-red-500" : ""
                    }`}
                    required
                  />
                  {renderError("email_id")}
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="date_of_birth"
                    className="text-xl text-[#889DB2] font-normal"
                  >
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="date_of_birth"
                    name="date_of_birth"
                    value={customerDetails.date_of_birth}
                    onChange={onInputChange}
                    className="px-4 py-[7px] text-[#F4F5F7] text-xl bg-[#2E4051] rounded-md outline-none focus:ring-[#008564] focus:border-[#008564]"
                    max={new Date().toISOString().split("T")[0]}
                  />
                </div>

                {/* Address Line 1 */}
                <div className="flex flex-col">
                  <label
                    htmlFor="address_line1"
                    className="text-xl text-[#889DB2] font-normal"
                  >
                    Address Line 1 *
                  </label>
                  <input
                    type="text"
                    id="address_line1"
                    name="address_line1"
                    value={customerDetails.address?.address_line1}
                    onChange={onInputChange}
                    className={`px-4 py-[7px] text-[#F4F5F7] text-xl bg-[#2E4051] rounded-md outline-none focus:ring-[#008564] focus:border-[#008564] ${
                      errors.address_line1 ? "border border-red-500" : ""
                    }`}
                    required
                  />
                  {renderError("address_line1")}
                </div>

                {/* Address Line 2 */}
                <div className="flex flex-col">
                  <label
                    htmlFor="address_line2"
                    className="text-xl text-[#889DB2] font-normal"
                  >
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    id="address_line2"
                    name="address_line2"
                    value={customerDetails.address?.address_line2}
                    onChange={onInputChange}
                    className="px-4 py-[7px] text-[#F4F5F7] text-xl bg-[#2E4051] rounded-md outline-none focus:ring-[#008564] focus:border-[#008564]"
                  />
                </div>

                {/* Pincode (you're using landmark input for pincode) */}
                <div className="flex flex-col">
                  <label
                    htmlFor="postal_code"
                    className="text-xl text-[#889DB2] font-normal"
                  >
                    Pincode *
                  </label>
                  <input
                    type="text"
                    id="postal_code"
                    name="postal_code"
                    value={customerDetails.address?.postal_code}
                    onChange={onInputChange}
                    className={`px-4 py-[7px] text-[#F4F5F7] text-xl bg-[#2E4051] rounded-md outline-none focus:ring-[#008564] focus:border-[#008564] ${
                      errors.postal_code ? "border border-red-500" : ""
                    }`}
                    maxLength={6}
                    required
                  />
                  {renderError("postal_code")}
                </div>

                {/* District (Province) */}
                <div className="flex flex-col">
                  <label
                    htmlFor="province"
                    className="text-xl text-[#889DB2] font-normal"
                  >
                    District
                  </label>
                  <input
                    type="text"
                    id="province"
                    name="province"
                    value={customerDetails.address?.province}
                    onChange={onInputChange}
                    className="px-4 py-[7px] text-[#F4F5F7] text-xl bg-[#2E4051] rounded-md outline-none focus:ring-[#008564] focus:border-[#008564]"
                  />
                </div>

                {/* City */}
                <div className="flex flex-col">
                  <label
                    htmlFor="city"
                    className="text-xl text-[#889DB2] font-normal"
                  >
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={customerDetails.address?.city}
                    onChange={onInputChange}
                    className={`px-4 py-[7px] text-[#F4F5F7] text-xl bg-[#2E4051] rounded-md outline-none focus:ring-[#008564] focus:border-[#008564] ${
                      errors.city ? "border border-red-500" : ""
                    }`}
                    required
                  />
                  {renderError("city")}
                </div>

                {/* Country */}
                <div className="flex flex-col">
                  <label
                    htmlFor="country"
                    className="text-xl text-[#889DB2] font-normal"
                  >
                    Country *
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={customerDetails.address?.country}
                    onChange={onInputChange}
                    className={`px-4 py-[7px] text-[#F4F5F7] text-xl bg-[#2E4051] rounded-md outline-none focus:ring-[#008564] focus:border-[#008564] ${
                      errors.country ? "border border-red-500" : ""
                    }`}
                    required
                  />
                  {renderError("country")}
                </div>
              </div>

              <div className="flex flex-row w-full gap-4 justify-end mt-6">
                <div className="flex">
                  <Button
                    children={isLoading ? "Saving..." : "Save"}
                    className="bg-[#008564] rounded-md text-[12px] sm:text-[11px] md:text-[15px] lg:text-[17px] xl:text-[18px] px-4 h-[40px] disabled:opacity-50"
                    onClick={handleSaveChanges}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};
